import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  LinearProgress,
} from "@material-ui/core";
import {
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// Quanto tempo a senha fica visivel antes de esconder sozinha de novo -
// pedido do usuario 2026-09-19 ("o copiar tem que ser automatico... deve
// ocultar em segundos isso pra todas as outras tambem").
const REVEAL_SECONDS = 8;

// "Senha Sync" nunca foi armazenada em lugar nenhum - sempre foi uma
// formula sobre a data/hora atual (ver o codigo original em
// arquivostecnica.github.io/gerador_senha). Mantido identico aqui, so'
// que exibido com o mesmo padrao de revelar/copiar/esconder das outras.
const computeSync = () => {
  const d = new Date();
  return "@" + (d.getDate() + d.getMonth() + 1) + (d.getHours() + 10);
};

// Campo de senha oculto por padrao - "Ver e copiar" revela, copia pra
// area de transferencia na hora, e esconde sozinho depois de
// REVEAL_SECONDS (barra de progresso mostra o tempo restante). Usado
// tanto pro campo grande (Senha Tecnica/Sync) quanto pras linhas
// compactas de Senhas Antigas via a prop "dense".
const RevealChip = ({ value, dense }) => {
  const [revealed, setRevealed] = useState(false);
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleReveal = async () => {
    if (!value) return;
    setRevealed(true);
    setProgress(100);
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`Copiado: ${value}`);
    } catch {
      toast.info(value);
    }
    clearInterval(intervalRef.current);
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const pct = Math.max(0, 100 - ((Date.now() - start) / (REVEAL_SECONDS * 1000)) * 100);
      setProgress(pct);
      if (pct <= 0) {
        clearInterval(intervalRef.current);
        setRevealed(false);
      }
    }, 100);
  };

  if (dense) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8, flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          style={{
            fontFamily: "monospace",
            flex: 1,
            color: revealed ? undefined : "#9e9e9e",
            letterSpacing: revealed ? 0 : 2,
          }}
        >
          {revealed ? value : "••••••••"}
        </Typography>
        <IconButton size="small" onClick={handleReveal} disabled={revealed || !value}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
      <Box flex={1} minWidth={0}>
        <Box
          style={{
            fontFamily: "monospace",
            fontSize: 20,
            fontWeight: 700,
            background: "rgba(91,79,224,0.08)",
            color: revealed ? "#5b4fe0" : "#9e9e9e",
            letterSpacing: revealed ? 0 : 4,
            padding: "10px 16px",
            borderRadius: 10,
          }}
        >
          {revealed ? value : "••••••••"}
        </Box>
        {revealed && (
          <LinearProgress variant="determinate" value={progress} style={{ height: 3, borderRadius: "0 0 10px 10px" }} />
        )}
      </Box>
      <Button
        variant="contained"
        color="primary"
        disabled={revealed || !value}
        onClick={handleReveal}
        startIcon={<VisibilityIcon />}
      >
        Ver e copiar
      </Button>
    </Box>
  );
};

// Pagina de atalho pros tecnicos consultarem a senha tecnica - antes
// vivia numa pagina publica no GitHub Pages
// (arquivostecnica.github.io/gerador_senha), editada manualmente no
// codigo-fonte toda vez que trocava. Pedido do usuario 2026-09-19: trazer
// pra dentro do Voicechat (atras de login), com troca facil que move o
// valor antigo pro historico sozinha, e "apenas os admins podem alterar
// a senha" - a engrenagem/tela de configuracao so' aparece pra admin.
const TechnicalPassword = () => {
  const { user } = useContext(AuthContext);
  const history_ = useHistory();
  const isAdmin = user?.profile === "admin";
  const [screen, setScreen] = useState("viewer");

  const [currentValue, setCurrentValue] = useState("");
  const [history, setHistory] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [links, setLinks] = useState([]);

  const [newPass, setNewPass] = useState("");
  const [saving, setSaving] = useState(false);
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkUrl, setNewLinkUrl] = useState("");

  const fetchPassword = useCallback(async () => {
    try {
      const { data } = await api.get("/technical-password");
      setCurrentValue(data.currentValue || "");
      setHistory(data.history || []);
      setUpdatedAt(data.updatedAt);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchLinks = useCallback(async () => {
    try {
      const { data } = await api.get("/technical-password/links");
      setLinks(data || []);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    fetchPassword();
    fetchLinks();
  }, [fetchPassword, fetchLinks]);

  const handleSavePassword = async () => {
    if (!newPass.trim() || saving) return;
    setSaving(true);
    try {
      await api.put("/technical-password", { value: newPass.trim() });
      toast.success("Senha atualizada — valor antigo movido pro histórico");
      setNewPass("");
      await fetchPassword();
      setScreen("viewer");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLinkName.trim() || !newLinkUrl.trim()) return;
    try {
      await api.post("/technical-password/links", { name: newLinkName.trim(), url: newLinkUrl.trim() });
      setNewLinkName("");
      setNewLinkUrl("");
      await fetchLinks();
      toast.success("Link adicionado — já apareceu na tela de visualização");
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveLink = async (linkId) => {
    try {
      await api.delete(`/technical-password/links/${linkId}`);
      await fetchLinks();
    } catch (err) {
      toastError(err);
    }
  };

  // Fora do LoggedInLayout de proposito (ver routes/index.js, registrada
  // junto com "/painel-tv") - pedido do usuario 2026-09-21 ("o link abre
  // o voice e nao so a tela de senhas"): e' uma pagina de atalho, exige
  // login mas nao deve vir com o menu/sidebar inteiro do sistema em
  // volta. Por isso o wrapper de tela cheia aqui em vez de MainContainer
  // sozinho (que assume estar dentro do layout com sidebar).
  return (
    <Box style={{ minHeight: "100vh", background: "#f5f5f7", display: "flex", flexDirection: "column" }}>
      <Box
        display="flex"
        alignItems="center"
        style={{ padding: "10px 16px", borderBottom: "1px solid #e0e0e0", background: "#fff" }}
      >
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => history_.push("/")}
        >
          Voicechat
        </Button>
      </Box>
      <MainContainer>
      <MainHeader>
        <Title>Senha Técnica</Title>
        {isAdmin && (
          <IconButton onClick={() => setScreen(screen === "viewer" ? "config" : "viewer")}>
            {screen === "viewer" ? <SettingsIcon /> : <ArrowBackIcon />}
          </IconButton>
        )}
      </MainHeader>

      <Box style={{ maxWidth: 720, margin: "0 auto", width: "100%", padding: 16, overflowY: "auto", overflowX: "hidden", }}>
        {screen === "viewer" ? (
          <>
            <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2">Senha Técnica</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                Senha principal usada nos atendimentos
              </Typography>
              <RevealChip value={currentValue} />
              {updatedAt && (
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 10 }}>
                  Alterada pela última vez em {new Date(updatedAt).toLocaleString("pt-BR")}
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2">Senha Sync</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                Calculada automaticamente pela data/hora — não precisa trocar manualmente
              </Typography>
              <RevealChip value={computeSync()} />
            </Paper>

            <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2">Senhas Antigas</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                Preenchido sozinho sempre que a Senha Técnica é trocada
              </Typography>
              {history.length === 0 ? (
                <Typography variant="body2" color="textSecondary">Nenhuma senha antiga ainda.</Typography>
              ) : (
                history.map((h, idx) => (
                  <Box
                    key={idx}
                    display="flex"
                    alignItems="center"
                    py={0.75}
                    style={{ borderTop: idx > 0 ? "1px solid #eee" : "none" }}
                  >
                    <RevealChip value={h.value} dense />
                    <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8, flexShrink: 0 }}>
                      {new Date(h.replacedAt).toLocaleDateString("pt-BR")}
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>

            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 12 }}>Links úteis</Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                {links.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">Nenhum link cadastrado.</Typography>
                ) : (
                  links.map((l) => (
                    <Button
                      key={l.id}
                      variant="outlined"
                      size="small"
                      component="a"
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {l.name}
                    </Button>
                  ))
                )}
              </Box>
            </Paper>
          </>
        ) : (
          <>
            <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Trocar Senha Técnica</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                O valor atual vira "senha antiga" automaticamente ao salvar — ninguém precisa editar histórico na mão.
              </Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <TextField
                  size="small"
                  variant="outlined"
                  fullWidth
                  placeholder="Nova senha"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  InputProps={{ style: { fontFamily: "monospace" } }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={saving || !newPass.trim()}
                  onClick={handleSavePassword}
                >
                  Salvar
                </Button>
              </Box>
            </Paper>

            <Paper variant="outlined" style={{ padding: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Links úteis</Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
                Cada link salvo aqui vira um botão automaticamente na tela de visualização
              </Typography>
              {links.map((l) => (
                <Box key={l.id} display="flex" alignItems="center" py={0.5} style={{ borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" style={{ fontWeight: 600, width: 130, flexShrink: 0 }} noWrap>
                    {l.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ flex: 1 }} noWrap>
                    {l.url}
                  </Typography>
                  <IconButton size="small" onClick={() => handleRemoveLink(l.id)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box display="flex" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  variant="outlined"
                  label="Nome"
                  value={newLinkName}
                  onChange={(e) => setNewLinkName(e.target.value)}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  label="URL"
                  fullWidth
                  style={{ minWidth: 180, flex: 1 }}
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddLink}
                  style={{ flexShrink: 0 }}
                >
                  Adicionar
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </Box>
      </MainContainer>
    </Box>
  );
};

export default TechnicalPassword;
